import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Container, Row, Col } from 'react-bootstrap';
import { Input, Fieldset, BackLink, Card, Details, Button, Textarea, ErrorSummary, Select } from 'nhsuk-react-components';
import { listConsents, createConsent, updateConsent, deleteConsent, getConsentDetails } from '../../actions/consentActions';
import { CONSENT_CREATE_RESET, CONSENT_UPDATE_RESET } from '../../constants/consentConstants';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Callout from '../../components/callout/Callout.component';
import Modal from '../../components/modal/Modal.component';
import ISODateInput from '../../components/date/ISODateInput.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

const emptyConsent = {
    consentType: '',
    consentDate: (new Date().toISOString()),
    consentDocument: '',
    consentPurpose: '',
    consentScope: '',
    consentAdditionalInfo: '',
};

const consentTypes = [
    "Full Access",
    "Limited Access",
    "Emergency Access",
    "Research Access",
    "Treatment Access",
    "Mental Health Access",
    "Donation Access",
];

export default function ConsentCRUDScreen({ history }) {
    const dispatch = useDispatch();

    useEffect(() => {
        document.title = "Edit Consents";
    }, []);

    useEffect(() => {
        dispatch({ type: CONSENT_CREATE_RESET });
        dispatch({ type: CONSENT_UPDATE_RESET });
    }, [dispatch]);

    const [consentListFilter, setconsentListFilter] = useState({ query: '', page: 1, filter: 'Name' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newConsent, setNewConsent] = useState({ ...emptyConsent });
    const [updatedConsent, setUpdatedConsent] = useState({});

    const consentList = useSelector((state) => state.consentList);
    const { loading, error, consents, currentPage, totalPages, totalCount } = consentList;

    const consentCreate = useSelector((state) => state.consentCreate);
    const { success: successCreate, error: consentCreateErrors, loading: consentCreateLoading } = consentCreate;

    const consentUpdate = useSelector((state) => state.consentUpdate);
    const { success: successUpdate, loading: consentUpdateProcessing } = consentUpdate;

    const consentDetails = useSelector((state) => state.consentDetails);
    const { consent: consentDet, loading: consentDetailsLoading } = consentDetails;

    useEffect(() => {
        dispatch(listConsents(consentListFilter));
    }, [dispatch, consentListFilter]);

    useEffect(() => {
        if (consentDet) {
            setUpdatedConsent({ ...consentDet });
        }
    }, [consentDet]);

    useEffect(() => {
        if (successCreate) {
            setShowCreateModal(false);
            setNewConsent({ ...emptyConsent, consentDate: (new Date().toISOString()) });
            dispatch(listConsents(consentListFilter));
            dispatch({ type: CONSENT_CREATE_RESET });
        }
    }, [successCreate, dispatch, consentListFilter]);

    const handlePageChange = (newPage) => {
        setconsentListFilter({ ...consentListFilter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setconsentListFilter({ ...consentListFilter, query: e.target.value, page: 1 });
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this consent?')) {
            dispatch(deleteConsent(id)).then(() => {
                dispatch(listConsents(consentListFilter));
                if (updatedConsent?.id === id) {
                    setShowEditModal(false);
                    setUpdatedConsent({});
                }
            });
        }
    };

    const handleEditConsent = (id) => {
        dispatch(getConsentDetails(id));
        setShowCreateModal(false);
        setShowEditModal(true);
    };

    const handleCloseEdit = () => {
        setShowEditModal(false);
        setUpdatedConsent({});
        dispatch({ type: CONSENT_UPDATE_RESET });
    };

    const handleCreateConsent = () => {
        dispatch(createConsent(newConsent));
    };

    const handleUpdateConsent = () => {
        dispatch(updateConsent(updatedConsent.id, updatedConsent)).then(() => {
            dispatch(getConsentDetails(updatedConsent.id));
            dispatch(listConsents(consentListFilter));
        });
    };

    const openCreateModal = () => {
        setNewConsent({ ...emptyConsent, consentDate: (new Date().toISOString()) });
        dispatch({ type: CONSENT_CREATE_RESET });
        setShowEditModal(false);
        setShowCreateModal(true);
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col md="12">
                            <div className="welcome-box">
                                <PageWrapper title="Consent Management" totalCount={totalCount} loading={loading} />
                                <BackLink
                                    asElement="a"
                                    href="#"
                                    onClick={() => history.goBack()}
                                >
                                    Return back
                                </BackLink>
                                <Button
                                    className="nhsuk-button--small nhsuk-u-margin-bottom-4"
                                    onClick={openCreateModal}
                                >
                                    + Create Consent
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <div className="nhsuk-grid-column-one-half">
                        <Card>
                            <Card.Content>
                                <Card.Heading className="nhsuk-heading-m">
                                    <Input
                                        id="search-consents"
                                        label="Search consents by type"
                                        name="search-consents"
                                        type="search"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearchChange(e);
                                            }
                                        }}
                                    />
                                </Card.Heading>
                                <Card.Description>
                                    <Fieldset>
                                        {consents && consents.length > 0 ? (
                                            consents.map((consent) => (
                                                <Details key={consent.id}>
                                                    <Details.Summary>
                                                        {consent.consentType}
                                                    </Details.Summary>
                                                    <Details.Text>
                                                        <p>
                                                            <strong>Last Updated: </strong>
                                                            {(consent.consentDate).split('T')[0]}
                                                        </p>
                                                        <div className="compact-admin-table__actions">
                                                            <Button
                                                                className="nhsuk-button--small"
                                                                disabled={consentDetailsLoading}
                                                                onClick={() => handleEditConsent(consent.id)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                secondary
                                                                className="nhsuk-button--small btn-delete"
                                                                onClick={() => handleDelete(consent.id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </Details.Text>
                                                </Details>
                                            ))
                                        ) : null}
                                    </Fieldset>
                                    {loading ? (
                                        <AppSpinner />
                                    ) : error ? (
                                        <p>{error}</p>
                                    ) : (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </Card.Description>
                            </Card.Content>
                        </Card>
                    </div>

                    <div className="nhsuk-grid-column-one-half" />
                </Container>
            </div>

            <Modal
                show={showCreateModal}
                handleClose={() => {
                    setShowCreateModal(false);
                    dispatch({ type: CONSENT_CREATE_RESET });
                }}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Create Consent</h2>
                {consentCreateErrors && (
                    <ErrorSummary aria-labelledby="error-summary-title" role="alert" tabIndex="-1">
                        <ErrorSummary.Title id="error-summary-title">There is a problem</ErrorSummary.Title>
                        <ErrorSummary.Body>
                            <ul className="nhsuk-list nhsuk-list--bullet">
                                {consentCreateErrors.map((error) => (
                                    <li key={error}>{error}</li>
                                ))}
                            </ul>
                        </ErrorSummary.Body>
                    </ErrorSummary>
                )}
                <Select
                    id="consentType-create"
                    label="Consent Type"
                    name="consentType-create"
                    onChange={(e) => setNewConsent({ ...newConsent, consentType: e.target.value })}
                    value={newConsent?.consentType || ''}
                    required
                >
                    <option value="">Select consent type</option>
                    {consentTypes.map((consentType) => (
                        <option key={consentType} value={consentType}>
                            {consentType}
                        </option>
                    ))}
                </Select>
                <ISODateInput
                    id="consentDate-create"
                    label="Last Updated"
                    name="consentDate-create"
                    value={newConsent?.consentDate}
                    onChange={(isoDate) => setNewConsent({ ...newConsent, consentDate: isoDate })}
                    required
                />
                <Textarea
                    id="consentDocument-create"
                    label="Consent Document"
                    name="consentDocument-create"
                    onChange={(e) => setNewConsent({ ...newConsent, consentDocument: e.target.value })}
                    value={newConsent?.consentDocument || ''}
                    style={{ height: '100px' }}
                />
                <Textarea
                    id="consentPurpose-create"
                    label="Consent Purpose"
                    name="consentPurpose-create"
                    onChange={(e) => setNewConsent({ ...newConsent, consentPurpose: e.target.value })}
                    value={newConsent?.consentPurpose || ''}
                    style={{ height: '100px' }}
                />
                <Textarea
                    id="consentScope-create"
                    label="Consent Scope"
                    name="consentScope-create"
                    onChange={(e) => setNewConsent({ ...newConsent, consentScope: e.target.value })}
                    value={newConsent?.consentScope || ''}
                    style={{ height: '100px' }}
                />
                <Textarea
                    id="consentAdditionalInfo-create"
                    label="Consent Additional Info"
                    name="consentAdditionalInfo-create"
                    onChange={(e) => setNewConsent({ ...newConsent, consentAdditionalInfo: e.target.value })}
                    value={newConsent?.consentAdditionalInfo || ''}
                    style={{ height: '100px' }}
                />
                <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                    <Button
                        className="nhsuk-button--small"
                        disabled={consentCreateLoading || consentUpdateProcessing}
                        onClick={handleCreateConsent}
                    >
                        Create Consent
                    </Button>
                    <Button
                        secondary
                        className="nhsuk-button--small"
                        onClick={() => {
                            setShowCreateModal(false);
                            dispatch({ type: CONSENT_CREATE_RESET });
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </Modal>

            <Modal
                show={showEditModal}
                handleClose={handleCloseEdit}
                theme="light"
                maxWidth="560px"
            >
                <h2 className="nhsuk-heading-l">Edit Consent</h2>
                {consentDetailsLoading ? (
                    <AppSpinner />
                ) : (
                    <>
                        <Select
                            id="consentType"
                            label="Consent Type"
                            name="consentType"
                            onChange={(e) => setUpdatedConsent({ ...updatedConsent, consentType: e.target.value })}
                            value={updatedConsent?.consentType || ''}
                            required
                        >
                            <option value="">Select consent type</option>
                            {consentTypes.map((consentType) => (
                                <option key={consentType} value={consentType}>
                                    {consentType}
                                </option>
                            ))}
                        </Select>
                        <ISODateInput
                            id="consentDate"
                            label="Last Updated"
                            name="consentDate"
                            value={updatedConsent?.consentDate}
                            onChange={(isoDate) => setUpdatedConsent({ ...updatedConsent, consentDate: isoDate })}
                            required
                        />
                        <Textarea
                            id="consentDocument"
                            label="Consent Description"
                            name="consentDocument"
                            onChange={(e) => setUpdatedConsent({ ...updatedConsent, consentDocument: e.target.value })}
                            value={updatedConsent?.consentDocument || ''}
                            style={{ height: '100px' }}
                        />
                        <Textarea
                            id="consentPurpose"
                            label="Consent Purpose"
                            name="consentPurpose"
                            onChange={(e) => setUpdatedConsent({ ...updatedConsent, consentPurpose: e.target.value })}
                            value={updatedConsent?.consentPurpose || ''}
                            style={{ height: '100px' }}
                        />
                        <Textarea
                            id="consentScope"
                            label="Consent Scope"
                            name="consentScope"
                            onChange={(e) => setUpdatedConsent({ ...updatedConsent, consentScope: e.target.value })}
                            value={updatedConsent?.consentScope || ''}
                            style={{ height: '100px' }}
                        />
                        <Textarea
                            id="consentAdditionalInfo"
                            label="Consent Additional Info"
                            name="consentAdditionalInfo"
                            onChange={(e) => setUpdatedConsent({ ...updatedConsent, consentAdditionalInfo: e.target.value })}
                            value={updatedConsent?.consentAdditionalInfo || ''}
                            style={{ height: '100px' }}
                        />
                        {successUpdate && (
                            <Callout
                                label="Success"
                                header="Success!"
                                autoDismiss={true}
                                dismissTime={5000}
                                time={new Date().toLocaleTimeString()}
                                title="Success!"
                                message="The consent was updated successfully."
                                mode="success"
                            />
                        )}
                        <div className="compact-admin-table__actions nhsuk-u-margin-top-4">
                            <Button
                                className="nhsuk-button--small"
                                disabled={consentUpdateProcessing}
                                onClick={handleUpdateConsent}
                            >
                                Save changes
                            </Button>
                            <Button
                                secondary
                                className="nhsuk-button--small"
                                onClick={handleCloseEdit}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </React.Fragment>
    );
}
